import { useEffect, useMemo, useState } from "react"
import {
  Megaphone,
  Users,
  Send,
  Sparkles,
  Wand2,
  Radio,
  MessagesSquare,
  CheckCircle2,
  Circle,
  X,
} from "lucide-react"
import { AnimatePresence, motion } from "framer-motion"
import { useDispatch, useSelector } from "react-redux"
import { isCatalogStore } from "../../utils/storeCategory"
import { allClientsData, allGeneralClientsData } from "../../store/actions"
import {
  createCampaignGroup,
  createSmsCampaign,
  getCampaignGroups,
  getManualCustomers,
  getSmsCampaigns,
  listCrmClients,
} from "../../api/auth/crm"
import { toast } from "react-hot-toast"
import { getCurrentPlan, getSmsPlanPricing } from "../../utils/permissions"
import SubscriptionModal from "../../components/subscrptions/SubscriptionModal"
import { runSubscriptionUpgrade } from "../../utils/subscriptionUpgrade"

export default function Campaigns() {
  const { userdata, allclientsdata, generalclientsdata } = useSelector((state: any) => state.UserReducer)
  const dispatch = useDispatch()
  const user = userdata?.user
  const currentPlan = getCurrentPlan(userdata)
  const availablePlans = userdata?.availablePlans
  const currentBillingCycle = userdata?.subscription?.billingCycle || userdata?.user?.subscriptionBillingCycle || "monthly"
  const store = userdata?.store
  const storeId = store?._id
  const isGeneralStore = isCatalogStore(store)

  const [groups, setGroups] = useState<any[]>([])
  const [campaigns, setCampaigns] = useState<any[]>([])
  const [manualCustomers, setManualCustomers] = useState<any[]>([])
  const [crmCustomers, setCrmCustomers] = useState<any[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [sendingCampaign, setSendingCampaign] = useState(false)

  const [groupName, setGroupName] = useState("")
  const [groupDescription, setGroupDescription] = useState("")
  const [selectedPhones, setSelectedPhones] = useState<string[]>([])
  const [contactSearch, setContactSearch] = useState("")

  const [campaignTitle, setCampaignTitle] = useState("")
  const [campaignMessage, setCampaignMessage] = useState("")
  const [selectedGroupId, setSelectedGroupId] = useState("")
  const [openSubscription, setOpenSubscription] = useState(false)
  const [openGroupModal, setOpenGroupModal] = useState(false)
  const [openComposerModal, setOpenComposerModal] = useState(false)

  useEffect(() => {
    if (!storeId) return
    if (isGeneralStore) {
      dispatch(allGeneralClientsData(storeId, 1, 100, false))
    } else {
      dispatch(allClientsData(storeId, 1, 100, false))
    }
  }, [storeId, isGeneralStore, dispatch])

  const refreshCampaignData = async () => {
    if (!storeId) return
    const [groupsResponse, campaignsResponse] = await Promise.all([
      getCampaignGroups(storeId),
      getSmsCampaigns(storeId, 1, 100),
    ])
    setGroups(groupsResponse?.data || [])
    setCampaigns(campaignsResponse?.data || [])
  }

  useEffect(() => {
    if (!storeId) return

    const loadCrmData = async () => {
      setLoadingGroups(true)
      try {
        const [groupsResponse, campaignsResponse, manualResponse, crmClientsResponse] = await Promise.all([
          getCampaignGroups(storeId),
          getSmsCampaigns(storeId, 1, 100),
          getManualCustomers(storeId),
          listCrmClients(storeId, false),
        ])
        setGroups(groupsResponse?.data || [])
        setCampaigns(campaignsResponse?.data || [])
        setManualCustomers(manualResponse?.data || [])
        setCrmCustomers(crmClientsResponse?.data || [])
      } catch (e: any) {
        console.error(e)
        toast.error(e?.message || "Impossible de charger les données CRM")
      } finally {
        setLoadingGroups(false)
      }
    }

    loadCrmData()
  }, [storeId])

  const allCustomers = useMemo(() => {
    const apiClients = (isGeneralStore ? generalclientsdata?.data || [] : allclientsdata?.data || []).map((c: any) => ({
      name: c.name || c.clientName || "Client",
      phone: c.phone || c.clientPhone,
      totalDebt: Number(c.totalDebt || 0),
      consentSms: true,
    }))

    const mappedManual = manualCustomers.map((c: any) => ({
      name: c.name,
      phone: c.phone,
      totalDebt: 0,
      consentSms: !!c.consentSms,
    }))

    const mappedCrm = crmCustomers.map((c: any) => ({
      name: c.name || "Client",
      phone: c.phone,
      totalDebt: Number(c.totalDebt || 0),
      consentSms: c.consentSms !== false,
    }))

    const byPhone = new Map<string, any>()
    for (const c of [...apiClients, ...mappedManual, ...mappedCrm]) {
      if (!c.phone) continue
      if (!byPhone.has(c.phone)) byPhone.set(c.phone, c)
      else {
        const prev = byPhone.get(c.phone)
        byPhone.set(c.phone, {
          ...prev,
          name: prev.name || c.name,
          totalDebt: Math.max(Number(prev.totalDebt || 0), Number(c.totalDebt || 0)),
          consentSms: Boolean(prev.consentSms) && Boolean(c.consentSms),
        })
      }
    }
    return Array.from(byPhone.values())
  }, [isGeneralStore, generalclientsdata, allclientsdata, manualCustomers, crmCustomers])

  const selectedGroup = groups.find((g) => g.id === selectedGroupId)
  const smsPlan = getSmsPlanPricing(currentPlan)
  const hasGroups = groups.length > 0

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupId("")
      return
    }
    const selectedStillExists = groups.some((g) => g.id === selectedGroupId)
    if (!selectedGroupId || !selectedStillExists) {
      setSelectedGroupId(groups[0].id)
    }
  }, [groups, selectedGroupId])

  const selectedRecipientsCount = useMemo(() => {
    if (!selectedGroup) return 0
    return allCustomers
      .filter((c) => selectedGroup.memberPhones.includes(c.phone))
      .filter((c) => c.consentSms)
      .length
  }, [allCustomers, selectedGroup])

  const smsUsedThisMonth = useMemo(() => {
    const now = new Date()
    const month = now.getMonth()
    const year = now.getFullYear()

    return campaigns.reduce((sum, c) => {
      const rawDate = c.sentAt || c.createdAt
      if (!rawDate) return sum
      const d = new Date(rawDate)
      if (Number.isNaN(d.getTime())) return sum
      if (d.getMonth() !== month || d.getFullYear() !== year) return sum
      const sentCount = Number(c.deliveredCount ?? c.totalRecipients ?? c.recipients?.length ?? 0)
      return sum + Math.max(0, sentCount)
    }, 0)
  }, [campaigns])

  const remainingIncluded = Math.max(smsPlan.monthlyIncluded - smsUsedThisMonth, 0)
  const payableSmsCount = Math.max(selectedRecipientsCount - remainingIncluded, 0)
  const estimatedCampaignCost = payableSmsCount * smsPlan.overagePrice
  const consentByPhone = useMemo(() => {
    const map = new Map<string, boolean>()
    allCustomers.forEach((c) => map.set(c.phone, Boolean(c.consentSms)))
    return map
  }, [allCustomers])

  const groupReachById = useMemo(() => {
    const map = new Map<string, number>()
    groups.forEach((g) => {
      const count = (g.memberPhones || []).reduce((sum: number, phone: string) => {
        return sum + (consentByPhone.get(phone) ? 1 : 0)
      }, 0)
      map.set(g.id, count)
    })
    return map
  }, [groups, consentByPhone])

  const filteredCustomers = useMemo(() => {
    const q = contactSearch.trim().toLowerCase()
    if (!q) return allCustomers
    return allCustomers.filter((c) => {
      return (
        String(c.name || "").toLowerCase().includes(q) ||
        String(c.phone || "").toLowerCase().includes(q)
      )
    })
  }, [allCustomers, contactSearch])

  const selectedFromFiltered = useMemo(() => {
    return filteredCustomers.filter((c) => selectedPhones.includes(c.phone)).length
  }, [filteredCustomers, selectedPhones])

  const smsCharacterCount = campaignMessage.length
  const smsSegments = Math.max(1, Math.ceil(Math.max(smsCharacterCount, 1) / 160))
  const isComposerReady = Boolean(selectedGroup && campaignTitle.trim() && campaignMessage.trim())
  const canCreateGroup = Boolean(groupName.trim() && selectedPhones.length > 0) && !loadingGroups

  const createGroup = async () => {
    if (!storeId) return
    if (!groupName.trim()) {
      toast.error("Nom du groupe requis")
      return
    }
    if (selectedPhones.length === 0) {
      toast.error("Ajoutez au moins un client au groupe")
      return
    }
    try {
      await createCampaignGroup(storeId, {
        name: groupName,
        description: groupDescription,
        memberPhones: selectedPhones,
      })
      await refreshCampaignData()
      setGroupName("")
      setGroupDescription("")
      setSelectedPhones([])
      toast.success("Groupe SMS créé")
    } catch (e: any) {
      console.error(e)
      toast.error(e?.message || "Impossible de créer le groupe")
    }
  }

  const sendCampaign = async () => {
    if (!storeId) return
    if (!selectedGroup) {
      toast.error("Sélectionnez un groupe")
      return
    }
    if (!campaignTitle.trim() || !campaignMessage.trim()) {
      toast.error("Titre et message requis")
      return
    }

    setSendingCampaign(true)
    try {
      const digitsPhone = String(user?.phone || "").replace(/[^\d]/g, "").trim()
      const payerEmail =
        String(user?.email || "").trim() || `gestock-${digitsPhone || Date.now()}@loven-soft.com`

      const response = await createSmsCampaign(storeId, {
        groupId: selectedGroup.id,
        title: campaignTitle.trim(),
        message: campaignMessage.trim(),
        email: payerEmail,
        paymentPhone: String(user?.phone || "").trim(),
        provider: "mobile_money",
      })
      await refreshCampaignData()
      setCampaignTitle("")
      setCampaignMessage("")
      setOpenComposerModal(false)

      if (response?.paymentRequired) {
        const paymentUrl =
          response?.payment?.authorization_url ||
          response?.campaign?.payment?.details?.paystack_init?.data?.authorization_url

        if (paymentUrl) {
          window.open(paymentUrl, "_blank")
          toast.success(
            `Paiement requis: ${Number(response?.payment?.amount || 0).toLocaleString()} FCFA.`
          )
        } else {
          toast(
            `Campagne en attente de paiement (${Number(response?.payment?.amount || 0).toLocaleString()} FCFA).`
          )
        }
      } else {
        toast.success(
          `Campagne envoyée à ${response?.campaign?.deliveredCount ?? response?.campaign?.totalRecipients ?? 0} client(s)`
        )
      }
    } catch (e: any) {
      console.error(e)
      const paymentUrl =
        e?.payment?.authorization_url ||
        e?.payment?.details?.paystack_init?.data?.authorization_url

      if (e?.paymentRequired && paymentUrl) {
        window.open(paymentUrl, "_blank")
        toast.success("Paiement requis avant l'envoi. Veuillez finaliser le paiement.")
      } else {
        toast.error(e?.message || "Impossible d'envoyer la campagne")
      }
    } finally {
      setSendingCampaign(false)
    }
  }

  const selectAllFilteredContacts = () => {
    const filteredPhones = filteredCustomers.map((c) => c.phone).filter(Boolean)
    if (filteredPhones.length === 0) return
    setSelectedPhones((prev) => Array.from(new Set([...prev, ...filteredPhones])))
  }

  const clearFilteredContacts = () => {
    const filteredSet = new Set(filteredCustomers.map((c) => c.phone))
    setSelectedPhones((prev) => prev.filter((phone) => !filteredSet.has(phone)))
  }

  const waitingPaymentCount = campaigns.filter((c) => c.status === "WAITING_PAYMENT").length
  const failedCampaignCount = campaigns.filter((c) => c.status === "FAILED").length
  const sentCampaignCount = Math.max(campaigns.length - waitingPaymentCount - failedCampaignCount, 0)

  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-600 via-blue-600 to-indigo-700 px-6 py-7 text-white shadow-xl">
        <div className="pointer-events-none absolute -top-14 -right-8 h-36 w-36 rounded-full bg-white/20 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-14 -left-10 h-40 w-40 rounded-full bg-cyan-300/20 blur-3xl" />
        <div className="relative">
          <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
            <Sparkles size={12} /> Broadcast Studio
          </p>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold">Campagnes SMS</h1>
          <p className="mt-2 text-sm text-white/85 max-w-2xl">
            Construisez vos groupes intelligents, composez des messages convaincants et diffusez vos nouveautés en quelques secondes.
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Step 1</p>
            <p className="mt-1 text-sm font-semibold text-slate-800 inline-flex items-center gap-2">
              {hasGroups ? <CheckCircle2 size={15} className="text-emerald-600" /> : <Circle size={15} className="text-slate-400" />}
              Créer un groupe
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Step 2</p>
            <p className="mt-1 text-sm font-semibold text-slate-800 inline-flex items-center gap-2">
              {selectedGroup ? <CheckCircle2 size={15} className="text-emerald-600" /> : <Circle size={15} className="text-slate-400" />}
              Choisir audience
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Step 3</p>
            <p className="mt-1 text-sm font-semibold text-slate-800 inline-flex items-center gap-2">
              {isComposerReady ? <CheckCircle2 size={15} className="text-emerald-600" /> : <Circle size={15} className="text-slate-400" />}
              Rédiger et envoyer
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Studio actions</p>
            <h2 className="text-xl font-semibold text-slate-800 mt-1">Pilotez vos campagnes avec style</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setOpenGroupModal(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
            >
              <Users size={15} />
              Nouveau groupe
            </button>
            <button
              onClick={() => {
                if (!hasGroups) {
                  toast.error("Créez d'abord un groupe pour composer une campagne.")
                  return
                }
                setOpenComposerModal(true)
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"
            >
              <Megaphone size={15} />
              Nouvelle campagne
            </button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs text-slate-500">Groupes</p>
            <p className="mt-1 text-2xl font-semibold text-slate-800">{groups.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs text-slate-500">Campagnes (mois)</p>
            <p className="mt-1 text-2xl font-semibold text-slate-800">{campaigns.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-emerald-50 p-4">
            <p className="text-xs text-emerald-700">SMS envoyés ce mois</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-800">{smsUsedThisMonth.toLocaleString()}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-blue-50 p-4">
            <p className="text-xs text-blue-700">SMS restants inclus</p>
            <p className="mt-1 text-2xl font-semibold text-blue-800">{remainingIncluded.toLocaleString()}</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {groups.slice(0, 3).map((g) => {
            const isActive = g.id === selectedGroupId
            return (
              <button
                key={g.id}
                onClick={() => setSelectedGroupId(g.id)}
                className={`rounded-2xl border p-4 text-left transition ${
                  isActive
                    ? "border-blue-300 bg-blue-50 shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <p className="font-semibold text-slate-800">{g.name}</p>
                <p className="mt-1 text-xs text-slate-500">{g.description || "Sans description"}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                    {g.memberPhones.length} contact(s)
                  </span>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                    {groupReachById.get(g.id) || 0} joignable(s)
                  </span>
                </div>
              </button>
            )
          })}
          {groups.length === 0 && (
            <div className="rounded-2xl border border-dashed border-blue-300 bg-blue-50/70 p-4 sm:p-5">
              <p className="text-sm font-semibold text-blue-800">Aucun groupe pour le moment</p>
              <p className="mt-1 text-sm text-blue-700">
                Ouvrez le modal pour créer votre premier groupe et débloquer la composition.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-5 sm:p-6 shadow-sm space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
          <MessagesSquare size={18} className="text-blue-600" />
            <h2 className="font-semibold text-slate-800">Historique campagnes</h2>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">
              Total: {campaigns.length}
            </span>
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">
              Envoyées: {sentCampaignCount}
            </span>
            <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-700">
              Paiement: {waitingPaymentCount}
            </span>
            <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-rose-700">
              Échecs: {failedCampaignCount}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {campaigns.map((c) => (
            <div
              key={c.id}
              className="rounded-2xl border border-white/70 bg-white p-4 shadow-sm transition hover:shadow-md hover:-translate-y-0.5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-800">{c.title}</p>
                  <p className="text-sm text-slate-600 mt-1">Groupe: {c.groupName}</p>
                </div>
                <span className="text-[11px] text-slate-500 whitespace-nowrap">
                  {new Date(c.sentAt || c.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-slate-700 mt-3 line-clamp-3">{c.message}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full ${
                    c.status === "WAITING_PAYMENT"
                      ? "bg-amber-100 text-amber-700"
                      : c.status === "FAILED"
                      ? "bg-rose-100 text-rose-700"
                      : c.status === "SCHEDULED"
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  {c.status || "SENT"}
                </span>
                {c?.payment?.required && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    Paiement: {Number(c?.payment?.amount || 0).toLocaleString()} {c?.payment?.currency || "XOF"}
                  </span>
                )}
                {c?.payment?.reference && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                    Ref: {c.payment.reference}
                  </span>
                )}
                {c.status === "WAITING_PAYMENT" && (
                  <button
                    onClick={() => {
                      const url = c?.payment?.details?.paystack_init?.data?.authorization_url
                      if (url) window.open(url, "_blank")
                      else toast.error("Lien de paiement indisponible pour cette campagne")
                    }}
                    className="text-[11px] rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-blue-700 hover:bg-blue-100"
                  >
                    Payer maintenant
                  </button>
                )}
              </div>
              <p className="text-xs text-emerald-700 mt-3">
                {c.deliveredCount ?? c.recipients?.length ?? 0} SMS envoyés
              </p>
            </div>
          ))}
          {campaigns.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 p-5 text-sm text-gray-500">
              Aucune campagne pour le moment.
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {openGroupModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white lg:ml-52 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-5 sm:p-6 space-y-5"
              initial={{ scale: 0.9, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 40 }}
            >
              <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Audience builder</p>
                <h3 className="text-lg font-semibold text-slate-800">Créer un groupe de campagne</h3>
              </div>
              <button
                onClick={() => setOpenGroupModal(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition"
              >
                <X size={16} />
              </button>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
              <div className="xl:col-span-5 space-y-4">
                <div className="space-y-3">
                  <input
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="Nom du groupe (ex: VIP, Clients Premium)"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
                  />
                  <input
                    value={groupDescription}
                    onChange={(e) => setGroupDescription(e.target.value)}
                    placeholder="Description du groupe"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
                  />
                </div>
                <button
                  onClick={createGroup}
                  disabled={!canCreateGroup}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-white hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Wand2 size={16} />
                  {loadingGroups ? "Création..." : "Créer ce groupe"}
                </button>
                <p className="text-xs text-slate-500">
                  {selectedPhones.length} contact(s) seront ajoutés au groupe.
                </p>
              </div>
              <div className="xl:col-span-7 rounded-2xl border border-slate-200">
                <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50 rounded-t-2xl">
                  <p className="text-sm font-medium text-slate-700">Sélection des contacts</p>
                  <span className="text-xs text-slate-500">
                    {selectedPhones.length} sélectionné(s), {selectedFromFiltered} dans la vue
                  </span>
                </div>
                <div className="p-3 border-b border-slate-100 bg-white">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Rechercher un contact par nom ou téléphone"
                      className="flex-1 min-w-[220px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-4 focus:ring-blue-100 outline-none"
                    />
                    <button
                      onClick={selectAllFilteredContacts}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                    >
                      Tout sélectionner
                    </button>
                    <button
                      onClick={clearFilteredContacts}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                    >
                      Retirer
                    </button>
                  </div>
                </div>
                <div className="p-3 max-h-[54vh] overflow-y-auto space-y-2">
                  {filteredCustomers.map((c) => (
                    <label
                      key={c.phone}
                      className="flex items-center justify-between gap-2 rounded-xl border border-slate-100 px-3 py-2 hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedPhones.includes(c.phone)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedPhones((prev) => [...prev, c.phone])
                            else setSelectedPhones((prev) => prev.filter((p) => p !== c.phone))
                          }}
                        />
                        <div>
                          <p className="text-sm font-medium text-slate-800">{c.name}</p>
                          <p className="text-xs text-slate-500">{c.phone}</p>
                        </div>
                      </div>
                      {!c.consentSms && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          No SMS consent
                        </span>
                      )}
                    </label>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <p className="text-sm text-gray-500">
                      {allCustomers.length === 0 ? "Aucun client disponible" : "Aucun contact trouvé pour cette recherche."}
                    </p>
                  )}
                </div>
              </div>
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {openComposerModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white lg:ml-52 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-5 sm:p-6 space-y-5"
              initial={{ scale: 0.9, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 40 }}
            >
              <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Campaign composer</p>
                <h3 className="text-lg font-semibold text-slate-800">Nouvelle campagne SMS</h3>
              </div>
              <button
                onClick={() => setOpenComposerModal(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition"
              >
                <X size={16} />
              </button>
              </div>
              <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-xl border bg-white p-3">
                    <p className="text-slate-500">Plan actuel</p>
                    <p className="mt-1 font-semibold text-slate-800">{currentPlan}</p>
                  </div>
                  <div className="rounded-xl border bg-white p-3">
                    <p className="text-slate-500">SMS inclus (mois)</p>
                    <p className="mt-1 font-semibold text-slate-800">
                      {Math.max(remainingIncluded, 0)} / {smsPlan.monthlyIncluded}
                    </p>
                  </div>
                  <div className="rounded-xl border bg-white p-3">
                    <p className="text-slate-500">Prix hors quota</p>
                    <p className="mt-1 font-semibold text-slate-800">{smsPlan.overagePrice} FCFA / SMS</p>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  Estimation campagne: {selectedRecipientsCount} destinataire(s), {payableSmsCount} facturable(s),
                  total estimé {estimatedCampaignCost.toLocaleString()} FCFA.
                </p>
                {!smsPlan.canSend && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                      Plan FREE: aucun SMS inclus. L&apos;envoi reste possible via paiement à l&apos;usage.
                    </p>
                    <button
                      onClick={() => setOpenSubscription(true)}
                      className="text-xs rounded-lg bg-blue-600 px-3 py-1.5 text-white hover:bg-blue-700"
                    >
                      Voir les plans
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">Groupe cible</label>
                  <select
                    value={selectedGroupId}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
                  >
                    <option value="">Choisir un groupe</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.memberPhones.length} contacts)
                      </option>
                    ))}
                  </select>

                  {selectedGroup && (
                    <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
                      <p className="text-sm font-semibold text-blue-800">{selectedGroup.name}</p>
                      <p className="text-xs text-blue-700 mt-1">{selectedGroup.description || "Sans description"}</p>
                      <p className="mt-2 text-xs text-blue-700">
                        Portée: {selectedRecipientsCount} contact(s) joignable(s) avec consentement SMS.
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Templates rapides</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
                        onClick={() =>
                          setCampaignMessage(
                            "Bonjour {firstName}, nouveaux arrivages chez {storeName} aujourd'hui. Passez nous voir."
                          )
                        }
                      >
                        Arrivages
                      </button>
                      <button
                        className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
                        onClick={() =>
                          setCampaignMessage(
                            "Promo en cours cette semaine chez {storeName}. Réductions spéciales sur une sélection."
                          )
                        }
                      >
                        Promo
                      </button>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Live Preview</p>
                  <div className="rounded-xl bg-white border border-slate-200 p-3">
                    <p className="text-xs text-slate-500">Titre</p>
                    <p className="font-medium text-slate-800">{campaignTitle || "Votre titre de campagne"}</p>
                  </div>
                  <div className="rounded-xl bg-white border border-slate-200 p-3">
                    <p className="text-xs text-slate-500">Message</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {campaignMessage || "Votre message apparaîtra ici..."}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <Radio size={12} /> Variables supportées: {"{firstName}"}, {"{storeName}"}
                  </p>
                  <p className="text-xs text-slate-500">
                    Longueur: {smsCharacterCount} caractère(s), environ {smsSegments} SMS segment(s).
                  </p>
                </div>
              </div>

              <input
                value={campaignTitle}
                onChange={(e) => setCampaignTitle(e.target.value)}
                placeholder="Titre campagne"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
              />
              <textarea
                value={campaignMessage}
                onChange={(e) => setCampaignMessage(e.target.value)}
                rows={5}
                placeholder="Message SMS"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 focus:ring-4 focus:ring-blue-100 outline-none"
              />

              <button
                onClick={sendCampaign}
                disabled={sendingCampaign || !isComposerReady}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-white hover:bg-emerald-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send size={16} />
                {sendingCampaign ? "Envoi..." : "Envoyer maintenant"}
              </button>
              {!isComposerReady && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Pour envoyer: choisissez un groupe, ajoutez un titre et rédigez le message.
                </p>
              )}
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <SubscriptionModal
        isOpen={openSubscription}
        currentPlan={currentPlan}
        currentBillingCycle={currentBillingCycle}
        plans={availablePlans}
        onClose={() => setOpenSubscription(false)}
        onUpgrade={(planKey, billingCycle) =>
          runSubscriptionUpgrade({
            planKey,
            billingCycle,
            currentPlan,
            currentBillingCycle,
            user,
            dispatch,
            onClose: () => setOpenSubscription(false),
          })
        }
      />
    </div>
  )
}
